import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, UtensilsCrossed, ShoppingBag, ReceiptText, User, ChefHat, BarChart3, Bike, Navigation, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useNotifications } from "@/contexts/NotificationsContext";

/** Mobile-only fixed bottom navigation, role-aware. Hidden on md+ screens and for admins. */
export default function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { count: cartCount } = useCart();
  const { badges } = useNotifications();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const role = user?.role || "client";

  if (role === "admin") return null;

  let items;
  if (role === "restaurant") {
    const tab = params.get("tab") || "today";
    const onDash = location.pathname === "/dashboard/restaurant";
    items = [
      { key: "orders", label: t("bottomNav.orders"), icon: ChefHat, to: "/dashboard/restaurant?tab=today", active: onDash && tab === "today", badge: badges.orders || 0 },
      { key: "menu", label: t("bottomNav.menu"), icon: UtensilsCrossed, to: "/dashboard/restaurant?tab=menu", active: onDash && tab === "menu" },
      { key: "revenue", label: t("bottomNav.revenue"), icon: BarChart3, to: "/dashboard/restaurant?tab=stats", active: onDash && tab === "stats" },
      { key: "account", label: t("bottomNav.account"), icon: User, to: "/partner-profile", active: location.pathname.startsWith("/partner-profile") },
    ];
  } else if (role === "driver") {
    const tab = params.get("tab") || "active";
    const focus = params.get("focus");
    const onDriver = location.pathname === "/driver";
    items = [
      { key: "courses", label: t("bottomNav.courses"), icon: Bike, to: "/driver?tab=active", active: onDriver && tab === "active" && !focus },
      { key: "current", label: t("bottomNav.current"), icon: Navigation, to: "/driver?tab=active&focus=current", active: onDriver && tab === "active" && focus === "current" },
      { key: "earnings", label: t("bottomNav.earnings"), icon: Wallet, to: "/driver?tab=earnings", active: onDriver && tab === "earnings" },
      { key: "account", label: t("bottomNav.account"), icon: User, to: "/partner-profile", active: location.pathname.startsWith("/partner-profile") },
    ];
  } else {
    // client + visitor
    const sec = params.get("sec");
    items = [
      { key: "home", label: t("bottomNav.home"), icon: Home, to: "/", active: location.pathname === "/" },
      { key: "restaurants", label: t("bottomNav.restaurants"), icon: UtensilsCrossed, to: "/restaurants", active: location.pathname.startsWith("/restaurants") },
      { key: "cart", label: t("bottomNav.cart"), icon: ShoppingBag, to: "/cart", active: location.pathname === "/cart" || location.pathname === "/checkout", badge: cartCount },
      { key: "orders", label: t("bottomNav.myOrders"), icon: ReceiptText, to: "/profile?sec=orders", active: location.pathname === "/profile" && sec === "orders" },
      { key: "account", label: t("bottomNav.account"), icon: User, to: user ? "/profile" : "/login", active: (location.pathname === "/profile" && sec !== "orders") || location.pathname === "/login" },
    ];
  }

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-lg border-t border-sand"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.key}
              to={it.to}
              data-testid={`bottomnav-${it.key}`}
              className={`relative flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${it.active ? "text-brand-red" : "text-mute hover:text-ink"}`}
            >
              <span className="relative">
                <Icon className="w-5 h-5" />
                {it.badge > 0 && (
                  <span data-testid={`bottomnav-${it.key}-badge`} className="absolute -top-1.5 -right-2 bg-brand-red text-white text-[9px] rounded-full min-w-[16px] h-4 px-1 grid place-items-center font-bold">
                    {it.badge > 9 ? "9+" : it.badge}
                  </span>
                )}
              </span>
              {it.label}
              {it.active && <span className="absolute top-0 inset-x-6 h-0.5 rounded-full bg-brand-red" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
